'use client'

import { useEffect, useState, useRef } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { Modal } from '@/Modal/Modal'
import { Button } from '@/General/Button'
import { ErrorBox } from '@/General/Forms/ErrorBox'
import { useStore } from '@/store'
import { useCaptcha } from './useCaptcha'
import { CheckIcon } from '@heroicons/react/24/outline'
import { setAdvertisingModalOpenState } from './advertisingModalState'
import { handleError } from './utils/errorHandling'
import { EMAIL_REGEX } from '@/validation'
import type {
  AdvertisingModalFormData,
  AdvertisingModalContent,
  AdvertisingModalConfig,
  AdvertisingModalEventCallbacks,
} from './types'

export interface AdvertisingModalProps {
  isOpen: boolean
  onClose: () => void
  content: AdvertisingModalContent
  config?: AdvertisingModalConfig
  callbacks?: AdvertisingModalEventCallbacks
  onSubmit: (data: AdvertisingModalFormData) => Promise<void>
}

export const AdvertisingModal = ({
  isOpen,
  onClose,
  content,
  config = {},
  callbacks = {},
  onSubmit,
}: AdvertisingModalProps) => {
  const user = useStore(state => state.user)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const callbacksRef = useRef(callbacks)
  const hasShownEventRef = useRef(false)
  const previousFormValuesRef = useRef<Record<string, string | undefined>>({})
  const hasTrackedInitialValuesRef = useRef(false)

  const {
    layout = 'side-by-side',
    maxWidth = '3xl',
    showCloseButton = true,
    closeOnBackdropClick = true,
    hideForLoggedInUsers = false,
    showFormForLoggedInUsers = true,
    enableCaptcha = true,
    redirectUrl,
    className = '',
  } = config

  const { verifyCaptcha } = useCaptcha({ enabled: enableCaptcha })

  const {
    title = 'Special Offer',
    introHtml,
    imageUrl,
    imageAlt = 'Promotional image',
    buttonText = 'Get Started',
    submitButtonText = 'Submit',
    cancelButtonText,
    successMessage = 'Thank you! Check your email for your download.',
    formFields = [
      {
        name: 'name',
        label: 'Full Name',
        type: 'text',
        required: true,
        validation: {
          pattern: /^[a-zA-Z]+\s+[a-zA-Z]+/,
          message: 'Please enter your first and last name',
        },
      },
      {
        name: 'email',
        label: 'Email Address',
        type: 'email',
        required: true,
        validation: {
          pattern: EMAIL_REGEX,
          message: 'Please enter a valid email address',
        },
      },
    ],
  } = content

  // Check if user is logged in and modal should be hidden
  const shouldHideModal = hideForLoggedInUsers && !!user
  const shouldShowForm = !user || showFormForLoggedInUsers

  // Keep callbacks ref in sync
  useEffect(() => {
    callbacksRef.current = callbacks
  }, [callbacks])

  // Cleanup redirect timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
      }
    }
  }, [])

  // Build default values from user data with proper null checks
  const defaultValues: Record<string, string> = {}
  if (user) {
    formFields.forEach(field => {
      if (field.name === 'email' && user.email) {
        defaultValues.email = user.email
      } else if (field.name === 'firstName' && user.firstName) {
        defaultValues.firstName = user.firstName
      } else if (field.name === 'lastName' && user.lastName) {
        defaultValues.lastName = user.lastName
      } else if (field.name === 'name' && user.firstName) {
        const lastName = user.lastName || ''
        const fullName = `${user.firstName} ${lastName}`.trim()
        // Only set if we have a meaningful name (not just "John ")
        if (fullName.length > user.firstName.length) {
          defaultValues.name = fullName
        } else {
          defaultValues.name = user.firstName
        }
      }
    })
  }

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<AdvertisingModalFormData>({
    defaultValues,
  })
  const formValues = useWatch({ control })

  // Track field changes
  useEffect(() => {
    const currentValues = formValues ?? {}

    if (!hasTrackedInitialValuesRef.current) {
      previousFormValuesRef.current = currentValues
      hasTrackedInitialValuesRef.current = true
      return
    }

    for (const [name, fieldValue] of Object.entries(currentValues)) {
      if (
        fieldValue !== previousFormValuesRef.current[name] &&
        typeof fieldValue === 'string' &&
        callbacksRef.current.onFormFieldChange
      ) {
        callbacksRef.current.onFormFieldChange(name, fieldValue)
      }
    }

    previousFormValuesRef.current = currentValues
  }, [formValues])

  // Track modal shown event (only once per open) and global state
  useEffect(() => {
    if (isOpen) {
      setAdvertisingModalOpenState(true)
      if (!hasShownEventRef.current && callbacksRef.current.onModalShown) {
        callbacksRef.current.onModalShown()
        hasShownEventRef.current = true
      }
      return
    }

    hasShownEventRef.current = false
    setAdvertisingModalOpenState(false)
  }, [isOpen])

  const handleFormSubmit = async (data: AdvertisingModalFormData) => {
    setIsLoading(true)

    if (callbacksRef.current.onFormSubmit) {
      callbacksRef.current.onFormSubmit(data)
    }

    try {
      // Verify bot protection if enabled
      await verifyCaptcha()

      await onSubmit(data)

      if (callbacksRef.current.onFormSuccess) {
        callbacksRef.current.onFormSuccess(data)
      }

      setIsSuccess(true)
      reset()

      // Redirect or auto-close after showing success message
      redirectTimeoutRef.current = setTimeout(() => {
        setIsSuccess(false)
        if (redirectUrl) {
          window.location.href = redirectUrl
        } else {
          onClose()
        }
      }, 2000)
    } catch (error) {
      handleError(
        error,
        callbacksRef.current.onFormError,
        'An error occurred. Please try again.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleButtonOnlyClick = () => {
    if (callbacksRef.current.onButtonClick) {
      callbacksRef.current.onButtonClick()
    }
    onClose()
  }

  const handleClose = () => {
    if (callbacksRef.current.onModalClose) {
      callbacksRef.current.onModalClose()
    }
    setIsSuccess(false)
    onClose()
  }

  // Don't render if should be hidden for logged in users
  if (shouldHideModal) {
    return null
  }

  const errorMessages = Object.values(errors)
    .map(error => error?.message)
    .filter((message): message is string => Boolean(message))

  const renderImage = () => {
    if (!imageUrl) return null

    return (
      <div
        className={`relative ${layout === 'stacked' ? 'w-full h-48 md:h-56' : 'w-full md:w-1/2 self-stretch min-h-[400px] -ml-4 -mt-4 md:min-h-[500px]'}`}
      >
        <img
          src={imageUrl}
          alt={imageAlt}
          className={`w-full h-full object-cover ${layout === 'stacked' ? 'rounded-t-2xl' : ''}`}
        />
      </div>
    )
  }

  const renderContent = () => {
    // Success state
    if (isSuccess) {
      return (
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
          </div>
          <p className="text-gray-600">{successMessage}</p>
        </div>
      )
    }

    // Button-only mode (logged in user, form hidden)
    if (!shouldShowForm) {
      return (
        <div className="space-y-6">
          {introHtml && (
            <div
              className="text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: introHtml }}
            />
          )}
          <Button
            onClick={handleButtonOnlyClick}
            color="green"
            size="lg"
            className="w-full"
          >
            {buttonText}
          </Button>
        </div>
      )
    }

    // Form mode
    return (
      <form
        onSubmit={event => {
          void handleSubmit(handleFormSubmit)(event)
        }}
        noValidate
        className="space-y-6"
      >
        {introHtml && (
          <div
            className="text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: introHtml }}
          />
        )}

        <ErrorBox errors={errorMessages} />

        <div className="space-y-4">
          {formFields.map((field, index) => {
            const fieldError = errors[field.name]
            const isTextarea = field.type === 'textarea'
            const isFirstField = index === 0

            return (
              <div key={field.name}>
                <label
                  htmlFor={field.name}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {field.label}
                  {field.required && (
                    <span className="text-red-600 ml-1">*</span>
                  )}
                </label>

                {isTextarea ? (
                  <textarea
                    {...register(field.name, {
                      required: field.required
                        ? `${field.label} is required`
                        : false,
                      pattern: field.validation?.pattern
                        ? {
                            value: field.validation.pattern,
                            message:
                              field.validation.message || 'Invalid format',
                          }
                        : undefined,
                      minLength: field.validation?.minLength
                        ? {
                            value: field.validation.minLength,
                            message: `Minimum ${field.validation.minLength} characters required`,
                          }
                        : undefined,
                      maxLength: field.validation?.maxLength
                        ? {
                            value: field.validation.maxLength,
                            message: `Maximum ${field.validation.maxLength} characters allowed`,
                          }
                        : undefined,
                    })}
                    id={field.name}
                    autoFocus={isFirstField}
                    placeholder={field.placeholder}
                    rows={4}
                    aria-invalid={!!fieldError}
                    aria-describedby={
                      fieldError ? `${field.name}-error` : undefined
                    }
                    className={`block w-full rounded border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset transition-all duration-200 text-base ${
                      fieldError
                        ? 'ring-red-500 focus:ring-red-500 focus:ring-2'
                        : 'ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-slate-500 hover:ring-gray-400'
                    }`}
                  />
                ) : (
                  <input
                    {...register(field.name, {
                      required: field.required
                        ? `${field.label} is required`
                        : false,
                      pattern: field.validation?.pattern
                        ? {
                            value: field.validation.pattern,
                            message:
                              field.validation.message || 'Invalid format',
                          }
                        : undefined,
                      minLength: field.validation?.minLength
                        ? {
                            value: field.validation.minLength,
                            message: `Minimum ${field.validation.minLength} characters required`,
                          }
                        : undefined,
                      maxLength: field.validation?.maxLength
                        ? {
                            value: field.validation.maxLength,
                            message: `Maximum ${field.validation.maxLength} characters allowed`,
                          }
                        : undefined,
                    })}
                    type={field.type || 'text'}
                    id={field.name}
                    autoFocus={isFirstField}
                    placeholder={field.placeholder}
                    aria-invalid={!!fieldError}
                    aria-describedby={
                      fieldError ? `${field.name}-error` : undefined
                    }
                    className={`block w-full rounded border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset transition-all duration-200 text-base ${
                      fieldError
                        ? 'ring-red-500 focus:ring-red-500 focus:ring-2'
                        : 'ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-slate-500 hover:ring-gray-400'
                    }`}
                  />
                )}

                {fieldError && (
                  <p
                    id={`${field.name}-error`}
                    className="text-red-600 text-sm mt-1"
                    role="alert"
                  >
                    {fieldError.message}
                  </p>
                )}
              </div>
            )
          })}
        </div>

        <div
          className={`flex gap-3 ${cancelButtonText ? 'justify-between' : 'justify-end'}`}
        >
          {cancelButtonText && (
            <Button
              type="button"
              color="gray"
              onClick={handleClose}
              disabled={isLoading}
            >
              {cancelButtonText}
            </Button>
          )}
          <Button
            type="submit"
            color="green"
            disabled={isLoading}
            className={!cancelButtonText ? 'w-full' : ''}
          >
            {isLoading ? 'Submitting...' : submitButtonText}
          </Button>
        </div>
      </form>
    )
  }

  const containerClass =
    layout === 'side-by-side'
      ? 'flex flex-col md:flex-row overflow-hidden'
      : 'flex flex-col overflow-hidden'

  const contentWrapperClass =
    layout === 'side-by-side'
      ? 'w-full md:w-1/2 p-6 md:p-8 overflow-y-auto'
      : 'w-full p-6 md:p-8'

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={undefined}
        showCloseButton={showCloseButton}
        closeOnBackdropClick={closeOnBackdropClick}
        maxWidth={maxWidth}
        className={className}
      >
        <div className={containerClass}>
          {layout === 'side-by-side' ? (
            <>
              {renderImage()}
              <div className={contentWrapperClass}>
                {title && (
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {title}
                  </h2>
                )}
                {renderContent()}
              </div>
            </>
          ) : (
            <>
              {renderImage()}
              <div className="w-full px-6 pt-6 pb-8 md:px-8">
                {title && (
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {title}
                  </h2>
                )}
                {renderContent()}
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  )
}
